import React from 'react';
import { AlertTriangle, TrendingDown, DollarSign, Info } from 'lucide-react';

const RiskAnalysisBlock = ({ 
  investmentAmount = null,
  currentValue = null,
  profit = null 
}) => {
  // Calculate risk metrics
  const investment = typeof investmentAmount === 'number' ? investmentAmount : parseFloat(investmentAmount) || 0;
  const value = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue) || investment;
  const profitValue = typeof profit === 'number' ? profit : parseFloat(profit) || 0;
  
  // If no valid investment data, don't render
  if (investment <= 0 && value <= 0) return null;
  
  const baseValue = value || investment;
  
  // Calculate 10% price drop loss
  const dropLoss = baseValue * 0.10;
  
  // Calculate fee impact range (2-7% of investment)
  const feeMin = investment * 0.02;
  const feeMax = investment * 0.07;
  
  // Calculate potential loss if market turns
  const worstCase = dropLoss + feeMax;

  const formatCurrency = (val) => {
    return Math.abs(val).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  return (
    <div 
      className="mt-4 p-4 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5 relative overflow-hidden"
      data-testid="risk-analysis-block"
    >
      {/* Subtle warning gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/3 via-transparent to-orange-500/3 pointer-events-none" />
      
      {/* Header */}
      <div className="relative flex items-center gap-2 mb-3">
        <AlertTriangle size={18} className="text-amber-400" />
        <h4 className="font-semibold text-amber-400">Risk Analysis</h4>
        <div className="ml-auto">
          <Info size={14} className="text-gray-500 cursor-help" title="Understand potential risks before trading" />
        </div>
      </div>
      
      {/* Risk Metrics */}
      <div className="relative space-y-2">
        {/* 10% Drop Scenario */}
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-900/40 border border-gray-700/30">
          <div className="flex items-center gap-2">
            <TrendingDown size={14} className="text-red-400" />
            <span className="text-sm text-gray-300">If price drops 10%</span>
          </div>
          <span className="text-sm font-semibold text-red-400">
            -{formatCurrency(dropLoss)}
          </span>
        </div>
        
        {/* Fee Impact */}
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-900/40 border border-gray-700/30">
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-orange-400" />
            <span className="text-sm text-gray-300">Estimated fee impact</span>
          </div>
          <span className="text-sm font-semibold text-orange-400">
            -{formatCurrency(feeMin)} to -{formatCurrency(feeMax)}
          </span>
        </div>
        
        {/* Worst Case */}
        {profitValue > 0 && (
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-400" />
              <span className="text-sm text-gray-300">Potential downside</span>
            </div>
            <span className="text-sm font-semibold text-red-400">
              -{formatCurrency(worstCase)}
            </span>
          </div>
        )}
      </div>
      
      {/* Reality Check Microcopy */}
      <p className="relative text-xs text-gray-500/70 mt-3 text-center italic">
        Most traders lose profits due to fees and poor execution timing.
      </p>
    </div>
  );
};

export default RiskAnalysisBlock;
